import * as Api from '@ViewModels';
import * as React from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { createContentStateWithHtmlStringValue } from '../../../../models/UiUtils';
import {
	invalidateAllHwcIndustryCategories,
	invalidateAllHwcIndustryCategoryTemplates,
	useHwcEditTemplateMutation,
	useHwcIndustryCategoryTemplate,
	useHwcTemplateAddStatusesMutation,
	useHwcTemplateRemoveStatusesMutation,
	useImageAttachmentMutation,
} from '../../../../queries';
import { KnownCategories } from '../../../../viewmodels/AppViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { useHwcTemplateForm } from './hooks/useHwcTemplateForm';
import { HwcTemplateForm } from './HwcTemplateForm';

export function HwcEditTemplate() {
	const { id } = useParams<{ id: string }>();
	const templateQuery = useHwcIndustryCategoryTemplate({ refetchOnWindowFocus: false, templateId: id });
	const formProps = useHwcTemplateForm();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('HwcEditTemplate');
	const fullscreenModal = useFullscreenModal();
	const formDispatch = formProps.dispatch;
	React.useEffect(() => {
		if (templateQuery.data) {
			formDispatch({
				type: 'INIT',
				value: {
					displayName: templateQuery.data.template.name,

					featured: templateQuery.data.statuses.some(s => s.category === 'Featured'),

					keywords: templateQuery.data.template.keywords?.join(', ') ?? '',
					postcardContent: Api.getContentHtmlStringValueFromRawRichTextContentState(
						templateQuery.data.template.content
					),

					providerProductIds: templateQuery.data.template.providerProductIds,
					// Don't deal with featured or archived categories in the form.

					selectedIndustryOptions: templateQuery.data.statuses.filter(
						s => s.category !== KnownCategories.Featured && s.category !== 'Archived'
					),

					summary: templateQuery.data.template.summary,

					templateImageUrl: templateQuery.data.template.image.publicUrl,
				},
			});
		}
	}, [templateQuery.data, formDispatch]);

	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to upload image'],
			});
			logger.logApiError('imageAttachmentMutation', error);
		},
	});
	const hwcTemplateMutation = useHwcEditTemplateMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to save template'],
			});
			logger.logApiError('hwcTemplateMutation', error);
		},
		onSuccess: () => {
			fullscreenModal.dismissModal();
		},
	});
	const templateRemoveStatusesMutation = useHwcTemplateRemoveStatusesMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to perform operation'],
			});
			logger.logApiError('RemoveStatuses-Error', error);
		},
	});
	const templateAddStatusesMutation = useHwcTemplateAddStatusesMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to perform operation'],
			});
			logger.logApiError('AddStatuses-Error', error);
		},
	});
	const mutationsInProgress = imageAttachmentMutation.isPending || hwcTemplateMutation.isPending;
	const saveAttachmentAndBody = async () => {
		// attachment from existing template has to be included in the payload if the user doesn't replace it on the form.
		// otherwise it will take the default value of undefined and the attachment will be deleted

		let attachments = templateQuery.data.template.attachments;

		let imageTypes = templateQuery.data.template.imageTypes;
		if (formProps.templateImageFile) {
			try {
				attachments = await imageAttachmentMutation.mutateAsync({ files: [formProps.templateImageFile] });
				imageTypes = { [Api.CardImageType.Face]: attachments[0].id };
			} catch {
				return;
			}
		}
		const content = createContentStateWithHtmlStringValue(formProps.postcardContent).getRawRichTextContent();
		hwcTemplateMutation.mutate({
			id,
			template: {
				attachments,
				content,
				id,
				keywords: formProps.keywords
					.split(',')

					.map(k => k.trim())

					.filter(k => !!k),
				name: formProps.displayName,
				providerProductIds: formProps.providerProductIds,
				summary: formProps.summary,
				templateType: Api.TemplateType.HandwrittenCard,
				...(!!attachments && {
					imageTypes,
				}),
			},
		});
	};
	const onSave = async () => {
		if (formProps.validate()) {
			await saveAttachmentAndBody();
			// Remove all statuses except archived since that is not editable from the form
			await templateRemoveStatusesMutation.mutateAsync({
				statuses: templateQuery.data.statuses.filter(s => s.category !== 'Archived'),
				templateId: id,
			});
			await templateAddStatusesMutation.mutateAsync({
				statuses: formProps.selectedIndustryOptions.map(option => {
					// Preserve the template's saved category status. If it didn't belong to the selected category before then it will be Draft

					const savedStatus = templateQuery.data.statuses.find(status => status.category === option.category);
					return {
						category: option.category,
						industry: option.industry,
						status: savedStatus ? savedStatus.status : Api.TemplateStatus.Draft,
					};
				}),
				templateId: id,
			});
			invalidateAllHwcIndustryCategoryTemplates();
			invalidateAllHwcIndustryCategories();
			toast.success('Template successfully saved');
		}
	};
	const onPublish = async () => {
		if (formProps.validate()) {
			await saveAttachmentAndBody();
			// Remove all statuses except archived since that is not editable from the form
			await templateRemoveStatusesMutation.mutateAsync({
				statuses: templateQuery.data.statuses,
				templateId: id,
			});
			await templateAddStatusesMutation.mutateAsync({
				statuses: formProps.selectedIndustryOptions

					.map(option => {
						return {
							category: option.category,
							industry: option.industry,
							status: Api.TemplateStatus.Published,
						};
					})
					.concat(
						formProps.featured
							? formProps.selectedIndustryOptions.map(({ industry }) => ({
									category: `Featured`,
									industry,
									status: Api.TemplateStatus.Published,
								}))
							: []
					),
				templateId: id,
			});
			invalidateAllHwcIndustryCategoryTemplates();
			invalidateAllHwcIndustryCategories();

			toast.success('Template successfully saved');
		}
	};
	return (
		<>
			<MultiContainerHeader fullscreenHeader='Edit Handwritten Card Template' />
			{templateQuery.isPending ? <LoadingSpinner type='large' /> : null}
			{!templateQuery.data && templateQuery.error ? <h2>Something went wrong loading template data</h2> : null}
			{templateQuery.data ? (
				<HwcTemplateForm
					{...formProps}
					onSave={onSave}
					onPublish={onPublish}
					mutationInProgress={mutationsInProgress}
				/>
			) : null}
		</>
	);
}
